import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    AlertTriangle,
    ChevronDown,
    Database,
    HardDrive,
    Layers,
    Loader2,
    ScanSearch,
    Server,
    Settings2,
    ShieldCheck,
} from "lucide-react";
import { Button } from "../../ui/button";
import { DatabaseCredentialDialog } from "./DatabaseCredentialDialog";
import { Card, CardContent } from "../../ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
    fetchSecretDiscoveryScanState,
    fetchSupportedScanTargetTypes,
    formatDuration,
    formatScanTimestamp,
    SCAN_STATE_LABELS,
    SCAN_TARGET_TYPE_LABELS,
    SCAN_TARGET_TYPE_ORDER,
    SUPPORTED_SECRET_DISCOVERY_PROVIDERS,
    SecretDiscoveryScanConflictError,
    SecretDiscoveryScanState,
    SecretDiscoveryScanTypeState,
    SecretDiscoveryTargetType,
    startSecretDiscoveryScan,
} from "../CloudAnalytics/secretDiscoveryService";

const RUNNING_POLL_INTERVAL_MS = 2000;
const PENDING_GRACE_MS = 60000;

export const SECRET_DISCOVERY_STATE_KEY = "secret-discovery-scan-state";
export const SECRET_DISCOVERY_COUNTS_KEY = "secret-discovery-footprint-counts";
export const SECRET_DISCOVERY_TARGET_TYPES_KEY = "secret-discovery-target-types";

interface UseSecretDiscoveryScanArgs {
    cloudProvider: string;
    tenantName?: string;
    projectId?: string;
}

export interface SecretDiscoveryScanController {
    supported: boolean;
    cloudProvider: string;
    tenantName?: string;
    projectId?: string;
    states: SecretDiscoveryScanTypeState[];
    stateByType: Map<SecretDiscoveryTargetType, SecretDiscoveryScanTypeState>;
    supportedTargetTypes: SecretDiscoveryTargetType[];
    isLoading: boolean;
    isStarting: boolean;
    isBusy: boolean;
    runningTargetTypes: SecretDiscoveryTargetType[];
    pendingTargetTypes: SecretDiscoveryTargetType[];
    lastScanEndedAt?: string;
    startScan: (targetTypes?: SecretDiscoveryTargetType[]) => void;
    startError: string | null;
    clearStartError: () => void;
}

export function useSecretDiscoveryScan({
    cloudProvider,
    tenantName,
    projectId,
}: UseSecretDiscoveryScanArgs): SecretDiscoveryScanController {
    const queryClient = useQueryClient();
    const [startError, setStartError] = useState<string | null>(null);
    const [pendingTargetTypes, setPendingTargetTypes] = useState<SecretDiscoveryTargetType[]>([]);
    const [pollUntil, setPollUntil] = useState(0);
    const previouslyRunningRef = useRef(false);
    const pendingBaselineRef = useRef<Map<SecretDiscoveryTargetType, string | undefined>>(new Map());

    const provider = String(cloudProvider || "").toLowerCase();
    const supported = SUPPORTED_SECRET_DISCOVERY_PROVIDERS.includes(provider)
        && Boolean(tenantName) && Boolean(projectId);

    const stateKey = [SECRET_DISCOVERY_STATE_KEY, provider, tenantName, projectId];

    const { data: states = [], isLoading } = useQuery(
        stateKey,
        () => fetchSecretDiscoveryScanState(provider, tenantName, projectId),
        {
            enabled: supported,
            refetchOnMount: "always",
            refetchOnWindowFocus: true,
            refetchIntervalInBackground: true,
            refetchInterval: (current: SecretDiscoveryScanTypeState[] | undefined) =>
                (current ?? []).some((entry) => entry.running)
                        || pendingTargetTypes.length > 0
                        || Date.now() < pollUntil
                    ? RUNNING_POLL_INTERVAL_MS
                    : false,
        },
    );

    const { data: supportedTargetTypes = [] } = useQuery(
        [SECRET_DISCOVERY_TARGET_TYPES_KEY, provider],
        () => fetchSupportedScanTargetTypes(provider),
        { enabled: supported, refetchOnWindowFocus: false, staleTime: Infinity },
    );

    const stateByType = useMemo(() => {
        const index = new Map<SecretDiscoveryTargetType, SecretDiscoveryScanTypeState>();
        states.forEach((entry) => index.set(entry.targetType, entry));
        return index;
    }, [states]);

    const runningTargetTypes = useMemo(
        () => states.filter((entry) => entry.running).map((entry) => entry.targetType),
        [states],
    );

    const lastScanEndedAt = useMemo(
        () =>
            states
                .map((entry) => entry.endedAt)
                .filter((value): value is string => Boolean(value))
                .sort()
                .pop(),
        [states],
    );

    const startMutation = useMutation(
        (targetTypes?: SecretDiscoveryTargetType[]) =>
            startSecretDiscoveryScan(provider, tenantName as string, projectId as string, targetTypes),
        {
            onMutate: (targetTypes?: SecretDiscoveryTargetType[]) => {
                const requested = targetTypes ?? supportedTargetTypes;
                setStartError(null);
                pendingBaselineRef.current = new Map(
                    requested.map((type) => [type, stateByType.get(type)?.scanId]));
                setPendingTargetTypes(requested);
                setPollUntil(Date.now() + PENDING_GRACE_MS);
            },
            onSuccess: () => {
                queryClient.invalidateQueries(stateKey);
            },
            onError: (error: any) => {
                pendingBaselineRef.current = new Map();
                setPendingTargetTypes([]);
                setPollUntil(0);
                setStartError(error instanceof SecretDiscoveryScanConflictError
                    ? error.message
                    : error?.message || "Unable to start the scan");
                queryClient.invalidateQueries(stateKey);
            },
            onSettled: () => {
                queryClient.invalidateQueries(stateKey);
            },
        },
    );

    useEffect(() => {
        if (pendingTargetTypes.length === 0 || startMutation.isLoading) return;
        const acknowledged = pendingTargetTypes.every((type) => {
            const entry = stateByType.get(type);
            return entry?.running || entry?.scanId !== pendingBaselineRef.current.get(type);
        });
        if (acknowledged || Date.now() >= pollUntil) {
            pendingBaselineRef.current = new Map();
            setPendingTargetTypes([]);
        }
    }, [stateByType, pendingTargetTypes, pollUntil, startMutation.isLoading]);

    useEffect(() => {
        const running = runningTargetTypes.length > 0;
        if (previouslyRunningRef.current && !running) {
            queryClient.invalidateQueries([SECRET_DISCOVERY_COUNTS_KEY]);
            queryClient.invalidateQueries(["secret-discovery"]);
        }
        previouslyRunningRef.current = running;
    }, [runningTargetTypes.length, queryClient]);

    const isStarting = startMutation.isLoading || pendingTargetTypes.length > 0;

    return {
        supported,
        cloudProvider: provider,
        states,
        stateByType,
        supportedTargetTypes,
        isLoading,
        isStarting,
        isBusy: isStarting || runningTargetTypes.length > 0,
        runningTargetTypes,
        pendingTargetTypes,
        lastScanEndedAt,
        startScan: (targetTypes?: SecretDiscoveryTargetType[]) => {
            if (!supported || startMutation.isLoading || runningTargetTypes.length > 0) return;
            startMutation.mutate(targetTypes);
        },
        startError,
        clearStartError: () => setStartError(null),
    };
}

const SCAN_TARGET_TYPE_ICONS: Record<SecretDiscoveryTargetType, typeof ShieldCheck> = {
    COMPUTE_VM: Server,
    STORAGE_BUCKET: HardDrive,
    DATABASE: Database,
    KUBERNETES_CLUSTER: Layers,
};

const MENU_SECTION_LABEL_CLASS =
    "px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground";

const MENU_ITEM_CLASS =
    "cursor-pointer gap-3 rounded-lg px-2 py-2 text-sm font-medium focus:bg-primary/10 focus:text-foreground data-[disabled]:cursor-not-allowed";

function MenuItemIcon({ icon: Icon }: { icon: typeof ShieldCheck }) {
    return (
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-muted/70 text-muted-foreground">
            <Icon className="h-4 w-4" />
        </span>
    );
}

export function StartSecretScanButton({
    controller,
}: {
    controller: SecretDiscoveryScanController;
}) {
    if (!controller.supported) return null;

    const { isBusy, isStarting, runningTargetTypes, supportedTargetTypes, startScan } = controller;
    const label = isStarting
        ? "Starting..."
        : runningTargetTypes.length > 0 ? "Scanning..." : "Start scan";
    const title = isBusy
        ? "A scan is already in progress for this project"
        : "Scan cloud resources for copies of these secrets";

    return (
        <div
            className="inline-flex items-stretch overflow-hidden rounded-lg border [border-color:var(--button-outline)] shadow-sm"
            data-testid="start-secret-scan-group"
        >
            <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-10 gap-2 rounded-none px-5 text-sm font-semibold"
                onClick={() => startScan()}
                disabled={isBusy}
                title={title}
                data-testid="button-start-secret-scan"
            >
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />}
                {label}
            </Button>

            <span aria-hidden className="my-2 w-px [background-color:var(--button-outline)]" />

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-10 rounded-none px-2.5 data-[state=open]:bg-muted/60"
                        disabled={isBusy}
                        title="Scan a single resource type"
                        aria-label="Choose which resources to scan"
                        data-testid="button-start-secret-scan-options"
                    >
                        <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="w-64 rounded-2xl border-border/60 p-1.5 shadow-xl"
                >
                    <DropdownMenuLabel className={MENU_SECTION_LABEL_CLASS}>
                        Full scan
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                        className={MENU_ITEM_CLASS}
                        onSelect={() => startScan()}
                        data-testid="menu-scan-all"
                    >
                        <MenuItemIcon icon={ScanSearch} />
                        <span className="flex min-w-0 flex-col">
                            <span>Scan all resources</span>
                            <span className="text-[11px] font-normal text-muted-foreground">
                                Runs every type, in order
                            </span>
                        </span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator className="-mx-1.5 my-1.5 bg-border/60" />

                    <DropdownMenuLabel className={MENU_SECTION_LABEL_CLASS}>
                        Single resource type
                    </DropdownMenuLabel>
                    {SCAN_TARGET_TYPE_ORDER.map((targetType) => {
                        const isAvailable = supportedTargetTypes.includes(targetType);
                        return (
                            <DropdownMenuItem
                                key={targetType}
                                className={MENU_ITEM_CLASS}
                                disabled={!isAvailable}
                                onSelect={() => startScan([targetType])}
                                title={isAvailable ? undefined : "Not available yet"}
                                data-testid={`menu-scan-${targetType}`}
                            >
                                <MenuItemIcon icon={SCAN_TARGET_TYPE_ICONS[targetType]} />
                                <span className="flex-1 truncate">
                                    {SCAN_TARGET_TYPE_LABELS[targetType]}
                                </span>
                                {isAvailable ? null : (
                                    <span className="flex-shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                                        Soon
                                    </span>
                                )}
                            </DropdownMenuItem>
                        );
                    })}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
const SUMMARY_CARD_ICONS: Record<SecretDiscoveryTargetType, typeof ShieldCheck> = {
    COMPUTE_VM: Server,
    STORAGE_BUCKET: HardDrive,
    DATABASE: Database,
    KUBERNETES_CLUSTER: Layers,
};

const SUMMARY_CARD_LABELS: Record<SecretDiscoveryTargetType, string> = {
    COMPUTE_VM: "Compute",
    STORAGE_BUCKET: "Storage Buckets",
    DATABASE: "Databases",
    KUBERNETES_CLUSTER: "Kubernetes",
};

const SUMMARY_CARD_SUBLABELS: Record<string, Record<SecretDiscoveryTargetType, string>> = {
    gcp: {
        COMPUTE_VM: "Compute Engine",
        STORAGE_BUCKET: "Cloud Storage",
        DATABASE: "Cloud SQL",
        KUBERNETES_CLUSTER: "GKE clusters",
    },
    aws: {
        COMPUTE_VM: "EC2 instances",
        STORAGE_BUCKET: "S3 buckets",
        DATABASE: "RDS instances",
        KUBERNETES_CLUSTER: "EKS clusters",
    },
    azure: {
        COMPUTE_VM: "Virtual machines",
        STORAGE_BUCKET: "Blob containers",
        DATABASE: "Azure SQL",
        KUBERNETES_CLUSTER: "AKS clusters",
    },
};

function summaryCardMeta(cloudProvider: string, targetType: SecretDiscoveryTargetType) {
    const sublabels = SUMMARY_CARD_SUBLABELS[cloudProvider] || SUMMARY_CARD_SUBLABELS.gcp;
    return {
        label: SUMMARY_CARD_LABELS[targetType],
        sublabel: sublabels[targetType],
        icon: SUMMARY_CARD_ICONS[targetType],
    };
}

function humanizeReason(reason: string): string {
    const spaced = reason.replace(/_/g, " ").toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function CardStat({
    label,
    value,
    tone,
    prominent = false,
}: {
    label: string;
    value: number;
    tone: "passed" | "failed" | "muted";
    prominent?: boolean;
}) {
    const toneClass =
        tone === "passed"
            ? "text-primary"
            : tone === "failed"
                ? "text-primary/75"
                : "text-muted-foreground";
    return (
        <div className="flex flex-col gap-1">
            <span
                className={`font-semibold leading-none tabular-nums ${
                    prominent ? "text-[32px]" : "text-xl"
                } ${value > 0 ? toneClass : "text-muted-foreground/55"}`}
            >
                {value}
            </span>
            <span className="text-[11px] leading-none text-muted-foreground">{label}</span>
        </div>
    );
}

const CARD_STATUS_STYLES: Record<SecretDiscoveryScanState, string> = {
    IN_PROGRESS: "border-primary/30 bg-primary/10 text-primary",
    COMPLETED: "border-primary/30 bg-primary/10 text-primary",
    COMPLETED_WITH_ERRORS: "border-primary/30 bg-primary/10 text-primary",
    FAILED: "border-border bg-muted text-muted-foreground",
    CANCELLED: "border-border bg-muted text-muted-foreground",
};

function CardStatusPill({
    state,
    pending,
}: {
    state?: SecretDiscoveryScanTypeState;
    pending: boolean;
}) {
    if (pending || state?.running) {
        return (
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${CARD_STATUS_STYLES.IN_PROGRESS}`}>
                <Loader2 className="h-3 w-3 animate-spin" />
                {pending && !state?.running ? "Starting" : "Scanning"}
            </span>
        );
    }
    if (!state?.everScanned || !state.status) {
        return (
            <span className="inline-flex items-center rounded-full border border-dashed border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                Never scanned
            </span>
        );
    }
    const status = state.status === "COMPLETED_WITH_ERRORS" ? "COMPLETED" : state.status;
    return (
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${CARD_STATUS_STYLES[status]}`}>
            {SCAN_STATE_LABELS[status]}
        </span>
    );
}

function OutcomeBar({
    passed,
    failed,
    skipped,
    planned,
    running,
}: {
    passed: number;
    failed: number;
    skipped: number;
    planned: number;
    running: boolean;
}) {
    const finished = passed + failed + skipped;
    const total = running ? Math.max(planned, finished) : finished;

    if (total === 0) {
        return <div className="mt-3 h-2 w-full rounded-full bg-muted" />;
    }

    const segments = [
        { key: "passed", value: passed, className: "bg-primary", label: "passed" },
        { key: "failed", value: failed, className: "bg-primary/55", label: "failed" },
        { key: "skipped", value: skipped, className: "bg-muted-foreground/45", label: "skipped" },
    ].filter((segment) => segment.value > 0);

    return (
        <div className="mt-3 flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted">
            {segments.map((segment) => (
                <div
                    key={segment.key}
                    className={`h-full transition-all duration-700 ease-out ${segment.className}`}
                    style={{ width: `${(segment.value / total) * 100}%` }}
                    title={`${segment.value} ${segment.label}`}
                />
            ))}
        </div>
    );
}

function ScanTypeCard({
    cloudProvider,
    targetType,
    state,
    isSupported,
    isPending,
    canStart,
    onScan,
    onConfigure,
}: {
    cloudProvider: string;
    targetType: SecretDiscoveryTargetType;
    state?: SecretDiscoveryScanTypeState;
    isSupported: boolean;
    isPending: boolean;
    canStart: boolean;
    onScan: () => void;
    onConfigure?: () => void;
}) {
    const meta = summaryCardMeta(cloudProvider, targetType);
    const Icon = meta.icon;
    const running = Boolean(state?.running) || isPending;
    const findings = state?.findingsCount ?? 0;

    return (
        <div
            className={`flex flex-col rounded-2xl border bg-white p-4 shadow-sm transition-all duration-300 dark:bg-card ${
                running
                    ? "border-primary/40 shadow-md ring-1 ring-primary/15"
                    : "portal-card-inner portal-card-hover"
            }`}
            data-testid={`scan-type-card-${targetType}`}
        >
            <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">{meta.label}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{meta.sublabel}</div>
                </div>
                {isSupported ? (
                    <CardStatusPill state={state} pending={isPending} />
                ) : (
                    <span className="flex-shrink-0 rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                        Coming soon
                    </span>
                )}
            </div>

            <div className="mt-4 flex items-baseline gap-7">
                <CardStat
                    label="Passed"
                    value={state?.targetsCompleted ?? 0}
                    tone="passed"
                    prominent
                />
                <CardStat label="Failed" value={state?.targetsFailed ?? 0} tone="failed" />
                {(state?.targetsSkipped ?? 0) > 0 ? (
                    <CardStat label="Skipped" value={state?.targetsSkipped ?? 0} tone="muted" />
                ) : null}
            </div>

            <OutcomeBar
                passed={state?.targetsCompleted ?? 0}
                failed={state?.targetsFailed ?? 0}
                skipped={state?.targetsSkipped ?? 0}
                planned={state?.targetsPlanned ?? 0}
                running={running}
            />

            <div className="mt-2 min-h-[16px] truncate text-[11px] text-muted-foreground">
                {!isSupported
                    ? "Scanning for this resource type is not available yet"
                    : running
                        ? (state?.targetsPlanned ?? 0) === 0
                            ? "Resolving secrets and planning resources..."
                            : `${(state?.targetsCompleted ?? 0) + (state?.targetsFailed ?? 0) + (state?.targetsSkipped ?? 0)} of ${state?.targetsPlanned} resources`
                        : state?.everScanned
                            ? `${formatScanTimestamp(state.endedAt || state.startedAt)}${
                                state.durationMillis ? ` · ${formatDuration(state.durationMillis)}` : ""
                            }`
                            : "Run a scan to see results here"}
            </div>

            {findings > 0 ? (
                <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1.5 text-[11px] font-medium text-primary">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                    <span>
                        {findings} secret {findings === 1 ? "location" : "locations"} found
                    </span>
                </div>
            ) : null}

            {(state?.failureReasons ?? []).length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-1">
                    {state?.failureReasons.slice(0, 3).map((entry) => (
                        <span
                            key={entry.reason}
                            className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                            title={`${entry.count} resource(s): ${humanizeReason(entry.reason)}`}
                        >
                            {humanizeReason(entry.reason)} · {entry.count}
                        </span>
                    ))}
                </div>
            ) : null}

            <div className="mt-4 flex-1" />
            <div className="flex gap-2">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 flex-1 gap-1.5 rounded-lg text-xs font-semibold"
                    disabled={!canStart}
                    onClick={onScan}
                    title={isSupported ? undefined : "Not available yet"}
                    data-testid={`scan-type-button-${targetType}`}
                >
                    {running ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <ScanSearch className="h-3.5 w-3.5" />
                    )}
                    {running ? "Scanning" : state?.everScanned ? "Rescan" : "Scan"}
                </Button>
                {onConfigure ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 rounded-lg p-0"
                        onClick={onConfigure}
                        title="Manage database credentials"
                        aria-label="Manage database credentials"
                        data-testid="scan-type-configure-DATABASE"
                    >
                        <Settings2 className="h-3.5 w-3.5" />
                    </Button>
                ) : null}
            </div>
        </div>
    );
}

export function SecretDiscoveryScanPanel({
    controller,
}: {
    controller: SecretDiscoveryScanController;
}) {
    const {
        supported,
        cloudProvider,
        stateByType,
        supportedTargetTypes,
        isLoading,
        isBusy,
        runningTargetTypes,
        pendingTargetTypes,
        lastScanEndedAt,
        startError,
        startScan,
        clearStartError,
    } = controller;

    const [credentialDialogOpen, setCredentialDialogOpen] = useState(false);

    const totalFindings = useMemo(
        () => controller.states.reduce((sum, entry) => sum + (entry.findingsCount ?? 0), 0),
        [controller.states],
    );

    const anyScanned = controller.states.some((entry) => entry.everScanned);

    if (!supported) return null;

    return (
        <Card className="cloud-card-surface rounded-2xl" data-testid="secret-scan-panel">
            <CardContent className="p-0">
                <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            {isBusy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <ScanSearch className="h-4 w-4" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground">
                                Secret footprint scan
                            </div>
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                {isLoading && !anyScanned
                                    ? "Loading scan history..."
                                    : runningTargetTypes.length > 0
                                        ? `Scanning ${runningTargetTypes
                                            .map((type) => SUMMARY_CARD_LABELS[type].toLowerCase())
                                            .join(", ")}...`
                                        : anyScanned
                                            ? `Last scan ${formatScanTimestamp(lastScanEndedAt)}`
                                            : "Each resource type is scanned and tracked on its own."}
                            </p>
                        </div>
                    </div>

                    {anyScanned ? (
                        <div className="flex flex-shrink-0 items-center gap-2.5">
                            <StatTile
                                label="Locations found"
                                value={totalFindings}
                                emphasis={totalFindings > 0}
                            />
                        </div>
                    ) : null}
                </div>

                {startError ? (
                    <div
                        className="flex items-start gap-2 border-t border-border/50 bg-primary/5 px-5 py-3 text-xs text-primary"
                        data-testid="secret-scan-start-error"
                    >
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span className="flex-1">{startError}</span>
                        <button type="button" className="underline" onClick={clearStartError}>
                            Dismiss
                        </button>
                    </div>
                ) : null}

                <div className="grid gap-3 border-t border-border px-5 py-5 sm:grid-cols-2 lg:grid-cols-3">
                    {SCAN_TARGET_TYPE_ORDER.map((targetType) => {
                        const isSupported = supportedTargetTypes.includes(targetType);
                        return (
                            <ScanTypeCard
                                key={targetType}
                                cloudProvider={cloudProvider}
                                targetType={targetType}
                                state={stateByType.get(targetType)}
                                isSupported={isSupported}
                                isPending={pendingTargetTypes.includes(targetType)}
                                canStart={isSupported && !isBusy}
                                onScan={() => startScan([targetType])}
                                onConfigure={isSupported && targetType === "DATABASE"
                                    ? () => setCredentialDialogOpen(true)
                                    : undefined}
                            />
                        );
                    })}
                </div>

                {controller.states.some((entry) => entry.errorMessage) ? (
                    <div className="flex items-start gap-2 border-t border-border/50 bg-muted/20 px-5 py-3 text-xs text-muted-foreground">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                            {controller.states.find((entry) => entry.errorMessage)?.errorMessage}
                        </span>
                    </div>
                ) : null}
            </CardContent>

            <DatabaseCredentialDialog
                open={credentialDialogOpen}
                onOpenChange={setCredentialDialogOpen}
                cloudProvider={cloudProvider}
                tenantName={controller.tenantName}
                projectId={controller.projectId}
            />
        </Card>
    );
}

function StatTile({
    label,
    value,
    emphasis = false,
}: {
    label: string;
    value: string | number;
    emphasis?: boolean;
}) {
    return (
        <div className="min-w-[104px] rounded-xl border border-border bg-white px-3.5 py-2 shadow-sm dark:bg-card">
            <div className="text-[11px] leading-none text-muted-foreground">{label}</div>
            <div className="mt-1.5 text-lg font-semibold leading-none">
                <span className={emphasis ? "text-primary" : "text-foreground"}>
                    {value}
                </span>
            </div>
        </div>
    );
}
